import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';

import Masonry from '@mui/lab/Masonry';

import useMediaQuery from '@mui/material/useMediaQuery';

import { ChooseProductCardSkeleton } from './ChooseProductCard';

const calcColumns = (isSmallScreen, products) => {
  if (isSmallScreen) return 1;
  return Math.min(products.length, 2);
};

const LoadingSkeleton = () => {
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const columns = useMemo(() => {
    return calcColumns(isSmallScreen, [1, 2, 3, 4]);
  }, [isSmallScreen]);

  return (
    <>
      <Masonry
        columns={columns}
        spacing={2}
        sequential
        sx={{ m: 0 /* component others calcs some negative margin */ }}
      >
        {[...Array(4)].map((_, index) => (
          <ChooseProductCardSkeleton key={index} />
        ))}
      </Masonry>
    </>
  );
};

const NoProductsAvailable = () => {
  const { t } = useTranslation();

  return (
    <>
      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: 'grey.50',
          border: '1px dashed',
          borderColor: (theme) => theme.palette.divider,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('choose_product.no_product.title', 'No Products Available')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t(
              'choose_product.no_product.message',
              'There are currently no products available for this resource. Please contact the provider or try again later.',
            )}
          </Typography>
        </Box>
      </Paper>
    </>
  );
};

export default function ChooseProductMasonry({
  products = [],
  isLoading,
  noProductsAvailable: NoProductsAvailableControlled = NoProductsAvailable,
  children,
}) {
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const columns = useMemo(() => {
    return calcColumns(isSmallScreen, products);
  }, [isSmallScreen, products]);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (products.length === 0) {
    return <NoProductsAvailableControlled />;
  }

  return (
    <>
      <Masonry
        columns={columns}
        spacing={2}
        sequential
        sx={{ m: 0 /* component others calcs some negative margin */ }}
      >
        {children}
      </Masonry>
    </>
  );
}

ChooseProductMasonry.propTypes = {
  products: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  noProductsAvailable: PropTypes.element,
};
